import { useCallback } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChartConfig } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { readStorage, writeStorage } from '../../../utils/hooks/useData';
import { ChartHandle } from '../chart-handle';

// The single source of "what edits are pending" for one focused chart. The agent's emitted edits land
// here as an ephemeral WORKING COPY — an unsaved ChartConfig that renders as a preview but structurally
// cannot touch the saved row until the user promotes it (Update / Save as a new chart / Discard). It
// lives in the react-query cache (not component state) because its producer — AgentDrawer, an app-shell
// Offcanvas — and its consumer — SingleChartView, a route — sit far apart in the tree and share no
// parent. Keyed by (company, handle) so each focused chart (or brand-new draft) has exactly one copy.
export const workingCopyKey = (company: string, handle: ChartHandle) => [
  'working-copy',
  company,
  handle
];

// The per-tab sessionStorage sidecar key. The sidecar is a DURABILITY layer, not the source of truth:
// the query cache is cleared on a page reload, so on the next mount the working copy is re-seeded from
// here — an unsaved preview (and a brand-new draft in particular) survives a same-tab refresh.
export const workingCopySidecarKey = (company: string, handle: ChartHandle) =>
  `working-copy:${company}:${handle}`;

export interface WorkingCopy {
  workingCopy: ChartConfig | null;
  setWorkingCopy: (config: ChartConfig) => void;
  clearWorkingCopy: () => void;
}

// Query options for one working copy, shared so EVERY reader of the same copy behaves identically —
// the focused single-chart view (useWorkingCopy below), each dashboard card (also useWorkingCopy), and
// the agent's focus-context useQueries over the dashboard's charts. If two observers on one key used
// different queryFns they'd race: one without the sidecar read returns null over a hydrating value. The
// queryFn only supplies the INITIAL value (there is no server fetch) — the current cache value, else the
// sessionStorage sidecar (so a reload re-hydrates the preview), else null. staleTime/gcTime Infinity keep
// it from ever refetching over a set value or being garbage-collected between a write and a read. The
// sidecar is read HERE (fresh, by the current handle's key) rather than via a useSessionStorageState hook
// because the handle changes as the user navigates — a useState-backed hook keeps its first key's value
// and would seed the wrong chart's copy after navigation.
export const workingCopyQueryOptions = (
  queryClient: QueryClient,
  company: string,
  handle: ChartHandle | null
) => {
  const key = handle ? workingCopyKey(company, handle) : ['working-copy', company, null];
  const sidecarKey = handle ? workingCopySidecarKey(company, handle) : null;
  return {
    queryKey: key,
    queryFn: (): ChartConfig | null =>
      queryClient.getQueryData<ChartConfig>(key) ??
      (sidecarKey ? readStorage<ChartConfig>(window.sessionStorage, sidecarKey) : undefined) ??
      null,
    enabled: Boolean(company && handle),
    staleTime: Infinity,
    gcTime: Infinity
  };
};

export const useWorkingCopy = (company: string, handle: ChartHandle | null): WorkingCopy => {
  const queryClient = useQueryClient();

  // Read reactively: setQueryData on this key re-renders every subscriber.
  const { data: workingCopy = null } = useQuery<ChartConfig | null>(
    workingCopyQueryOptions(queryClient, company, handle)
  );

  const setWorkingCopy = useCallback(
    (config: ChartConfig) => {
      if (handle) {
        queryClient.setQueryData(workingCopyKey(company, handle), config);
        writeStorage(window.sessionStorage, workingCopySidecarKey(company, handle), config);
      }
    },
    [queryClient, company, handle]
  );

  const clearWorkingCopy = useCallback(() => {
    if (handle) {
      queryClient.setQueryData(workingCopyKey(company, handle), null);
      window.sessionStorage.removeItem(workingCopySidecarKey(company, handle));
    }
  }, [queryClient, company, handle]);

  return { workingCopy, setWorkingCopy, clearWorkingCopy };
};

// Seed a working copy for a handle whose component isn't mounted yet — used when AgentDrawer mints a
// brand-new `draft:<uuid>` and must stash the emit before navigating to the draft view (which then owns
// it via useWorkingCopy). Writes both the reactive cache and the durability sidecar, matching the hook.
export const seedWorkingCopy = (
  queryClient: QueryClient,
  company: string,
  handle: ChartHandle,
  config: ChartConfig
): void => {
  queryClient.setQueryData(workingCopyKey(company, handle), config);
  writeStorage(window.sessionStorage, workingCopySidecarKey(company, handle), config);
};

// Clear a working copy for a handle whose component isn't (necessarily) mounted — used when
// Discarding a whole dashboard, which needs to drop every one of its charts' pending edits in one go
// rather than mounting useWorkingCopy per chart (hooks can't run in a loop). Mirrors clearWorkingCopy.
export const clearWorkingCopyFor = (
  queryClient: QueryClient,
  company: string,
  handle: ChartHandle
): void => {
  queryClient.setQueryData(workingCopyKey(company, handle), null);
  window.sessionStorage.removeItem(workingCopySidecarKey(company, handle));
};
